import React, { useContext, useState } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Form, FormikContextType, FormikProvider } from 'formik';
import FormikTextField from '../../components/formikComponents/FormikTextField';
import { ADDRESS_DRAWER_WIDTH } from '../../constants';
import { UIContext } from '../../context/UIContext';
import FormikPhoneNumber from '../../components/formikComponents/FormikPhoneNumber';
import AddressFormWithGoogleAutocomplete from '../../components/AddressFormWithGoogleAutocomplete';

const AddressForm = ({
  formik,
  cancelAction,
  isSubmitting,
}: {
  formik: FormikContextType<any>;
  cancelAction: () => void;
  isSubmitting: boolean;
}) => {
  const { t } = useTranslation();
  const { isMobile, iOS, colorMode } = useContext(UIContext);
  const [validMobileNumber, setValidMobileNumber] = useState<boolean>(false);
  const [phoneNumberError, setPhoneNumberError] = useState<string>('');

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit} style={{ marginTop: '16px' }}>
        <FormikTextField
          label={t('screens.locations2.addressName')}
          id="name"
          name="name"
          formName="locations2"
          autoComplete="organization"
          required
        />
        <FormikTextField
          label={t('screens.locations2.description')}
          id="description"
          name="description"
          formName="locations2"
        />

        <AddressFormWithGoogleAutocomplete
          formik={formik}
          formName="locations2"
          translationKey="screens.locations2"
          extraAddressLine={
            <FormikTextField
              label={t('screens.locations2.address2')}
              id="address2"
              name="address2"
              formName="locations2"
              autoComplete="address-line2"
            />
          }
          allFieldsFullWidth
        />

        <FormikTextField
          label={t('screens.locations2.emailAddress')}
          id="emailAddress"
          name="emailAddress"
          formName="locations2"
          autoComplete="email"
          required
        />
        <FormikPhoneNumber
          label={t('screens.locations2.phoneNumber')}
          id="phoneNumber"
          name="phoneNumber"
          formName="locations2"
          autoComplete="tel"
          required
          type="tel"
          error={phoneNumberError}
          clearError={() => {
            setPhoneNumberError('');
          }}
          setError={() => setPhoneNumberError(t('validation.invalidPhoneNumber'))}
          valid={validMobileNumber}
          setValid={setValidMobileNumber}
          defaultCountry={
            formik?.values?.country && formik?.values?.country?.length === 2
              ? formik?.values?.country?.toLowerCase()
              : 'gb'
          }
        />
        <Box
          sx={{
            display: 'flex',
            ...(!iOS && {
              width: `calc(${isMobile ? '100%' : `${ADDRESS_DRAWER_WIDTH}px`} - 32px)`,
              position: 'fixed',
              bottom: 0,
              right: 0,
              padding: '8px 16px',
              borderTop: (theme) =>
                `1px ${
                  colorMode === 'light' ? theme.palette.grey['12p'] : theme.palette.grey['23p']
                } solid`,
            }),
            ...(iOS && {
              marginTop: 2,
            }),
            zIndex: 1,
            justifyContent: 'flex-end',
            backgroundColor: 'background.paper',
          }}
        >
          <Button color="primary" type="button" onClick={cancelAction} sx={{ marginRight: '16px' }}>
            {t('actions.cancel')}
          </Button>
          <Button color="primary" variant="contained" type="submit" disableElevation>
            {isSubmitting && (
              <>
                <CircularProgress color="inherit" size="20px" sx={{ marginRight: '8px' }} />
                {t('actions.saving')}
              </>
            )}
            {!isSubmitting && t('actions.save')}
          </Button>
        </Box>
      </Form>
    </FormikProvider>
  );
};
export default AddressForm;
